import type { AnySceneObjectT } from "tweenly"
import ImageIcon from "@mui/icons-material/Image"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Tooltip,
    Typography,
} from "@mui/material"
import { t } from "i18next"
import { useCallback, useEffect, useMemo } from "react"
import { useController } from "react-hook-form"
import * as yup from "yup"
import { checkImage, isImageSrcAttribute, isImageSrcUrl } from "../../../helpers/images.helpers"
import { wrapUrlForProxy } from "../../../helpers/url.helpers"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppSelector } from "../../../hooks/useRedux"
import { useReduxForm } from "../../../hooks/useReduxForm"
import ProxyAlert from "../../ProxyAlert"
import { Transition } from "../../common/Transition"
import Switch from "../../common/form/Switch"
import TextField from "../../common/form/TextField"
import SelectAttributes from "../DataSource/SelectAttributes"
import { submitFormOnEnter } from "../../../helpers/dialog.helpers"

interface Props {
    open: boolean
    onClose: () => void
    parentId: string | null
    object?: AnySceneObjectT
}
const DEFAULT_VALUES: ImageSrcDataT = {
    imgSrc: "",
    proxy: false,
}
type ImageSrcDataT = {
    imgSrc: string
    proxy: boolean
}

const ImageSrcDialog = ({ open, onClose, parentId, object }: Props) => {
    const {
        readImageFileFromURL,
        replaceImageSourceFromUrl,
        readImageFileFromAsset,
        replaceImageSourceFromAsset,
    } = useObjectActions()
    const assets = useAppSelector((state) => state.assets.data)
    const defaultData = useMemo(() => {
        return {
            ...DEFAULT_VALUES,
            imgSrc: object?.fill
                .find((f) => f.property === "backgroundImage")
                ?.value.replace(/url\("|"\)/g, ""),
        }
    }, [object])

    const onSubmitImageSrc = async (data: ImageSrcDataT) => {
        const finalUrl = data.proxy ? wrapUrlForProxy(data.imgSrc, "image") : data.imgSrc
        if (isImageSrcAttribute(data.imgSrc)) {
            if (object) {
                replaceImageSourceFromAsset(data.imgSrc, object)
            } else {
                readImageFileFromAsset(data.imgSrc, parentId)
            }
        } else {
            if (object) {
                replaceImageSourceFromUrl(finalUrl, object)
            } else {
                readImageFileFromURL(finalUrl, parentId)
            }
        }
        handleClose()
    }

    const checkAttributeExist = useCallback(
        (imgSrc: string) => {
            const [slug, name] = imgSrc.replace(/{|}/g, "").split("/")
            const asset = assets.find((asset) => asset.slug === slug)
            if (asset) {
                return asset.attributes.find((attribute) => attribute.name === name) ? true : false
            } else return false
        },
        [assets]
    )

    const imageSrcSchema = useMemo(
        () =>
            yup.object({
                imgSrc: yup.string().test(
                    "is-url-or-attribute",
                    () => t("editor:imageSrcDialog.imgSrcError"),
                    async (value, context) => {
                        if (!value) return true
                        if (isImageSrcAttribute(value)) return checkAttributeExist(value)
                        if (!isImageSrcUrl(value)) {
                            return context.createError({
                                message: t("editor:imageSrcDialog.invalidUrlError") as string,
                            })
                        }
                        const proxyValue = (context.parent as ImageSrcDataT)?.proxy
                        return await checkImage(value, proxyValue)
                    }
                ),
                proxy: yup.boolean(),
            }),
        [checkAttributeExist]
    )

    const {
        control,
        reset,
        trigger,
        watch,
        handleSubmit,
        formState: { errors, isSubmitting, isValid, touchedFields },
    } = useReduxForm({
        schema: imageSrcSchema,
        defaultValues: defaultData,
    })

    const imgSrc = useController({ name: "imgSrc", control })

    const watchedImgSrc = watch("imgSrc")
    const watchedProxy = watch("proxy")
    // errors are shown once the field was blurred, so the user is not warned while still typing
    const imgSrcError = touchedFields.imgSrc ? errors.imgSrc : undefined
    const showProxySwitch = Boolean(imgSrcError) && isImageSrcUrl(watchedImgSrc)

    useEffect(() => {
        trigger("imgSrc")
    }, [watchedImgSrc, watchedProxy, trigger])

    const handleSetImageFromAttribute = (imageUrlFromAssets: string) => {
        imgSrc.field.onChange(imageUrlFromAssets)
    }

    const handleClose = () => {
        reset(DEFAULT_VALUES)
        onClose()
    }

    return (
        <Dialog
            open={open}
            onKeyDown={submitFormOnEnter}
            onClose={handleClose}
            TransitionComponent={Transition}
            keepMounted
            sx={{ zIndex: 1400 }}
            maxWidth={"xl"}
        >
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmitImageSrc)}
                m={{ xs: 0, sm: 3 }}
                width={{ xs: "100%", md: 800 }}
            >
                <DialogTitle component={"h4"} sx={{ display: "flex", alignItems: "center", pb: 2 }}>
                    <ImageIcon sx={{ mr: 1 }} fontSize="large" />
                    {object
                        ? t("editor:imageSrcDialog.replaceTitle")
                        : t("editor:imageSrcDialog.newTitle")}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ height: 20, ml: 0.5, mb: 1 }}>
                        <Typography>{t("editor:imageSrcDialog.URLImageTitle")}</Typography>
                    </Box>
                    <Grid container>
                        <Grid item md={12}>
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                                <TextField
                                    control={control}
                                    fullWidth
                                    name="imgSrc"
                                    label={t("image src")}
                                    error={imgSrcError}
                                    disabled={false}
                                />
                                <Box mt={0.4}>
                                    <SelectAttributes handleChange={handleSetImageFromAttribute} />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item md={12} ml={0.5}>
                            {showProxySwitch ? (
                                <>
                                    <ProxyAlert
                                        title={t("editor:imageSrcDialog.proxyAlert.title")}
                                        causesTitle={t(
                                            "editor:imageSrcDialog.proxyAlert.causesTitle"
                                        )}
                                        reasons={[
                                            {
                                                title: t(
                                                    "editor:imageSrcDialog.proxyAlert.reasons.invalidTitle"
                                                ),
                                                description: t(
                                                    "editor:imageSrcDialog.proxyAlert.reasons.invalidDescription"
                                                ),
                                            },
                                            {
                                                title: t(
                                                    "editor:imageSrcDialog.proxyAlert.reasons.blockedTitle"
                                                ),
                                            },
                                        ]}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <Switch
                                            control={control}
                                            name="proxy"
                                            error={errors.proxy}
                                            label={
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <span>
                                                        {t(
                                                            "editor:imageSrcDialog.proxyAlert.switchLabel"
                                                        )}
                                                    </span>
                                                    <Tooltip
                                                        title={t(
                                                            "editor:imageSrcDialog.proxyAlert.switchTooltip"
                                                        )}
                                                        arrow
                                                    >
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </Tooltip>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </>
                            ) : null}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="text"
                        color="inherit"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        {t("disagree")}
                    </Button>
                    <Button type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                        {object
                            ? t("editor:imageSrcDialog.buttonReplace")
                            : t("editor:imageSrcDialog.buttonCreate")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default ImageSrcDialog
